import { FC, useState } from 'react';
import { useProductsIntegrationEntities } from '../../../../../../../../../../../../hooks/requests/use-products-integration-entities';
import { Box, Button, Stack, Typography } from '@mui/material';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { BaseIntegrationEntity, INTEGRATION_PROVIDERS, NetSuiteProductEntity } from '@vy/types';
import { isNotEmptyString, Optional } from '@vy/utils';
import { VyIcon } from '@vy/ui-design-system';
import { EditErpProductSettingsDialog } from './EditErpProductSettingsDialog';
import { useNetSuiteClasses } from '../../../../../../../../../../../../hooks/requests/integrations';
import { VyDivider } from '../../../../../../../../../../../../components/new-design/atoms';

type ErpProductSettingsAccordionContentProps = {
  invoiceId: string;
  customerId: string;
  disabled: boolean;
};

export const ErpProductSettingsAccordionContent: FC<ErpProductSettingsAccordionContentProps> = ({
  invoiceId,
  customerId,
  disabled,
}) => {
  useNetSuiteClasses();
  const { data } = useProductsIntegrationEntities(invoiceId, {
    suspense: true,
  });

  const [openDialogId, setOpenDialogId] = useState<string | null>(null);
  const products = data?.products ?? [];

  const handleOpen = (lineItemId: string) => {
    setOpenDialogId(lineItemId);
  };

  const handleClose = () => {
    setOpenDialogId(null);
  };

  return (
    <Stack direction="column" sx={{
      gap: "16px"
    }}>
      {products.map((product) => {
        const netSuiteEntity = getNetSuiteEntity(product.integrationEntities ?? []);
        const hasErpMapping = netSuiteEntity !== undefined;

        return (
          <Box
            key={product.lineItemId}
            sx={{
              border: '1px solid #E8EBF2',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: '#F5F6FA',
                padding: '0 16px',
                height: '32px'
              }}>
              <Typography
                sx={{
                  lineHeight: "20px",
                  fontSize: "14px",
                  color: "text.secondary"
                }}>
                {product.name}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<VyIcon icon={hasErpMapping ? 'Pen' : 'PlusMedium'} />}
                onClick={() => handleOpen(product.lineItemId)}
                disabled={disabled}
                sx={{ fontSize: '12px', lineHeight: '16px' }}
              >
                {hasErpMapping ? 'Edit' : 'Add'}
              </Button>
            </Stack>
            <Stack
              direction="column"
              sx={{
                gap: "8px",
                padding: '15px 16px'
              }}>
              {hasErpMapping ? (
                <>
                  {isNotEmptyString(netSuiteEntity?.metadata.name) && (
                    <Typography
                      sx={{
                        lineHeight: "20px",
                        fontSize: "14px"
                      }}>
                      <Box component="span" sx={{
                        fontWeight: 600
                      }}>
                        ERP Name:
                      </Box>{' '}
                      {netSuiteEntity.metadata.name}
                    </Typography>
                  )}

                  {isNotEmptyString(netSuiteEntity?.metadata.name) &&
                    isNotEmptyString(netSuiteEntity?.metadata.classMetadata?.name) && <VyDivider />}

                  {isNotEmptyString(netSuiteEntity?.metadata.classMetadata?.name) && (
                    <Typography
                      sx={{
                        lineHeight: "20px",
                        fontSize: "14px"
                      }}>
                      <Box component="span" sx={{
                        fontWeight: 600
                      }}>
                        Class:
                      </Box>{' '}
                      {netSuiteEntity.metadata.classMetadata.name}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  sx={{
                    lineHeight: "20px",
                    fontSize: "14px",
                    color: "text.secondary"
                  }}>
                  No custom fields here. Click Add to add
                </Typography>
              )}
            </Stack>
            <EditErpProductSettingsDialog
              open={openDialogId === product.lineItemId}
              onClose={handleClose}
              product={product}
              invoiceId={invoiceId}
              customerId={customerId}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

function getNetSuiteEntity(integrationEntities: BaseIntegrationEntity[]): Optional<NetSuiteProductEntity> {
  return findIntegrationByProviderInEntities<NetSuiteProductEntity>(
    integrationEntities,
    INTEGRATION_PROVIDERS.netSuite,
  );
}
